export interface Event {
  id: string;
  date: Date;
  endDate?: Date;
  time: string;
  title: string;
  url?: string;
  ticketUrl?: string;
  location: string;
  partner?: {
    name: string;
    links: {
      label: string;
      url: string;
    }[];
  };
}

// Define all events
export const allEvents: Event[] = [
  {
    id: 'nakama-cafe-2025',
    date: new Date('2025-11-29T12:00:00'),
    time: '12 pm - 5 pm',
    title: 'Nakama Café Event',
    location: 'Wells Rd, Nakama Café Parking Lot'
  },
  {
    id: 'orange-park-farmers-2025',
    date: new Date('2025-12-07T10:00:00'),
    time: '10 am - 3 pm',
    title: 'Orange Park Farmers Market',
    location: 'Orange Park'
  },
  {
    id: 'new-year-party-2026',
    date: new Date('2026-01-17T12:00:00'),
    time: '12 pm - 2:30 pm',
    title: 'JAJ Annual NEW YEAR PARTY',
    location: 'Fleming Island Plantation Club House'
  },
  {
    id: 'nakama-festival-2026',
    date: new Date('2026-03-07T12:00:00'),
    time: '12 pm - 5 pm',
    title: 'Japan "Nakama" Festival',
    location: 'Nakama Café'
  },
  {
    id: 'wasabi-con-2026',
    date: new Date('2026-10-09T00:00:00'),
    endDate: new Date('2026-10-11T23:59:59'),
    time: 'Friday 3 PM - 10 PM; Saturday 10 AM - 8 PM; Sunday 9 AM - 6 PM',
    title: 'Wasabi Con',
    url: 'https://jax.wasabicon.com/',
    ticketUrl: 'https://www.tixr.com/pr/JapaneseAssociation/160456',
    location: 'Prime F. Osborn III Convention Center, 1000 Water St, Jacksonville, FL 32204',
    partner: {
      name: 'WasabiCon',
      links: [
        {
          label: 'Bluesky',
          url: 'https://bsky.app/profile/wasabicon.com'
        },
        {
          label: 'Facebook',
          url: 'https://www.facebook.com/WasabiCon'
        },
        {
          label: 'Instagram',
          url: 'https://www.instagram.com/wasabicon'
        },
        {
          label: 'TikTok',
          url: 'https://www.tiktok.com/@wasabicon'
        },
        {
          label: 'X (Twitter)',
          url: 'https://x.com/wasabicon'
        }
      ]
    }
  }
];

export interface SortedEvents {
  upcomingEvents: Event[];
  pastEvents: Event[];
}

/**
 * Sorts events into upcoming and past based on current date
 * - Upcoming events are sorted by date (ascending - earliest first)
 * - Past events are sorted by date (descending - most recent first)
 * - An event is considered "past" only if it ended more than 1 day ago
 *   (i.e., if today is more than 1 day after the event date)
 */
export const sortEvents = (events: Event[]): SortedEvents => {
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

  const upcoming: Event[] = [];
  const past: Event[] = [];

  events.forEach(event => {
    const eventDate = new Date(event.endDate ?? event.date);
    eventDate.setHours(0, 0, 0, 0);

    // Calculate the date 1 day after the event ended (event ends at end of event date)
    const oneDayAfterEvent = new Date(eventDate);
    oneDayAfterEvent.setDate(oneDayAfterEvent.getDate() + 1);

    // Event is past only if today is more than 1 day after the event ended
    if (now > oneDayAfterEvent) {
      past.push(event);
    } else {
      upcoming.push(event);
    }
  });

  // Sort upcoming events by date (ascending)
  upcoming.sort((a, b) => a.date.getTime() - b.date.getTime());
  // Sort past events by date (descending - most recent first)
  past.sort((a, b) => b.date.getTime() - a.date.getTime());

  return { upcomingEvents: upcoming, pastEvents: past };
};

/**
 * Formats a date to a readable string
 */
export const formatEventDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Formats an event's date or date range to a readable string
 */
export const formatEventDateRange = (event: Event): string => {
  if (!event.endDate) {
    return formatEventDate(event.date);
  }

  const start = event.date;
  const end = event.endDate;

  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  ) {
    return `${start.toLocaleDateString('en-US', {
      month: 'long',
    })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
  }

  return `${formatEventDate(start)} - ${formatEventDate(end)}`;
};
